import React, { useEffect, useState } from 'react';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import { useDispatch } from 'react-redux';
import * as Icon from 'react-feather';
import { Button, Col, Modal, ModalBody, ModalFooter, ModalHeader, Row } from 'reactstrap';

import {
  whatsappMetaFlowAdd,
  whatsappMetaFlowUpdate,
  whatsappMetaTemplatesOptionsGet
} from '../../../store/slices/tenantSlices/configurationSlice';
import FormSelect from '../../../components/form/FormSelect';
import FormInput from '../../../components/form/FormInput';

const AddEditMetaFlowModal = ({ isOpen, onCloseClick, isEdit = false, editRecord = null }) => {
  const dispatch = useDispatch();

  const flowOptionInitialValue = {
    opt_name: 'Fail',
    opt_id: ''
  };

  const [whatsappBotTemplatesOptions, setWhatsappBotTemplatesOptions] = useState([]);
  const [flowOptions, setFlowOptions] = useState([flowOptionInitialValue]);

  const initialValues = {
    main_template_id: '',
    is_store: ''
  };

  const validationSchema = Yup.object({
    main_template_id: Yup.string().required('template is required'),
    is_store: Yup.string().required('store name is required')
  });

  const onSubmit = async () => {
    if (flowOptions?.some((x) => !x.opt_id || !x.opt_name)) {
      toast.error('Please fill up flow options');
      return;
    }

    const buildPayload = () => {
      return {
        ...values,
        option: flowOptions.reduce((acc, { opt_name, opt_id }) => {
          if (opt_name && opt_id) acc[opt_name] = opt_id;
          return acc;
        }, {})
      };
    };

    try {
      const payload = buildPayload();

      let response;
      if (isEdit) {
        response = await dispatch(
          whatsappMetaFlowUpdate({ ...payload, id: editRecord.id })
        ).unwrap();
      } else {
        response = await dispatch(whatsappMetaFlowAdd(payload)).unwrap();
      }

      if (response && response?.message) {
        toast.success(`Record ${isEdit ? 'Updated' : 'Added'} Successfully`);
        onCloseClick(true);
        resetForm();
      } else {
        toast.error(response?.error || 'Something went wrong');
      }
    } catch (error) {
      console.log('error', error);
      toast.error('An unexpected error occurred');
    }
  };

  const fetchWhatsappTemplatesOptions = async () => {
    try {
      const response = await dispatch(whatsappMetaTemplatesOptionsGet()).unwrap();
      if (response && response.data) {
        const options = response.data.map((val) => ({
          label: val.name,
          value: val.id
        }));
        setWhatsappBotTemplatesOptions(options);
      } else {
        setWhatsappBotTemplatesOptions([]);
      }
    } catch (error) {
      setWhatsappBotTemplatesOptions([]);
      console.log('error', error);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchWhatsappTemplatesOptions();
    }
  }, [isOpen]);

  const { values, handleChange, handleBlur, handleSubmit, touched, errors, resetForm, setValues } =
    useFormik({
      initialValues,
      validationSchema,
      onSubmit
    });

  useEffect(() => {
    if (isEdit && editRecord) {
      setValues((prev) => ({
        ...prev,
        main_template_id: editRecord.main_template_id,
        is_store: editRecord.is_store
      }));

      const editFlowOptions = editRecord.option;
      const optionsArray = Object.entries(editFlowOptions || {}).map(([opt_name, opt_id]) => ({
        opt_name,
        opt_id
      }));
      setFlowOptions(optionsArray);
    }
  }, [editRecord]);

  const handleAdd = () => {
    setFlowOptions([
      ...flowOptions,
      {
        opt_name: '',
        opt_id: ''
      }
    ]);
  };

  const handleDelete = (index) => {
    const updatedOptions = flowOptions.filter((_, i) => i !== index);
    setFlowOptions(updatedOptions);
  };

  const handleOptionChange = (index, event) => {
    const { name, value } = event.target;
    const updatedOptions = [...flowOptions];
    updatedOptions[index] = { ...updatedOptions[index], [name]: value };
    setFlowOptions(updatedOptions);
  };

  return (
    <Modal isOpen={isOpen} size="xl" centered>
      <ModalHeader className="justify-content-center py-2">
        <div className="fs-5">{isEdit ? 'Edit Whatsapp Meta Flow' : 'Add Whatsapp Meta Flow'}</div>
      </ModalHeader>
      <ModalBody className="px-4">
        <form id="whatsapp-templates" onSubmit={handleSubmit}>
          <Row>
            <Col lg="4">
              <FormSelect
                name="main_template_id"
                label="Main Template"
                placeholder="Select Main Template"
                value={values.main_template_id}
                options={whatsappBotTemplatesOptions}
                onChange={handleChange}
                onBlur={handleBlur}
                touched={touched}
                errors={errors}
              />
            </Col>
            <Col lg="4">
              <FormInput
                name="is_store"
                label="Store As"
                placeholder="Enter Store As"
                value={values.is_store}
                onChange={handleChange}
                onBlur={handleBlur}
                touched={touched}
                errors={errors}
              />
            </Col>
          </Row>
          <Row>
            <Col lg="12" className="border-lightGray" style={{ borderBottom: '1px solid' }}>
              <h5 className="mt-4 text-dark fw-bolder">Flow Options</h5>
            </Col>
          </Row>
          {flowOptions?.map((item, index, array) => {
            return (
              <Row key={index} className="pt-3">
                <Col lg="4">
                  <FormInput
                    name="opt_name"
                    label="Option"
                    placeholder="Enter Option"
                    value={item.opt_name}
                    onChange={(e) => handleOptionChange(index, e)}
                    disabled={index === 0}
                  />
                </Col>
                <Col lg="4">
                  <FormSelect
                    name="opt_id"
                    label="Template"
                    placeholder="Select Template"
                    value={item.opt_id}
                    options={whatsappBotTemplatesOptions}
                    onChange={(e) => handleOptionChange(index, e)}
                  />
                </Col>
                <Col lg="4" className="d-flex align-items-center gap-4 pt-2">
                  {array?.length > 1 && index !== 0 ? (
                    <Icon.Trash
                      size={20}
                      onClick={() => handleDelete(index)}
                      className="cursor-pointer text-danger"
                    />
                  ) : null}
                  {array?.length - 1 === index ? (
                    <Icon.Plus className="cursor-pointer text-info" size={20} onClick={handleAdd} />
                  ) : null}
                </Col>
              </Row>
            );
          })}
        </form>
      </ModalBody>
      <ModalFooter>
        <Button
          onClick={() => {
            onCloseClick();
            setFlowOptions([flowOptionInitialValue]);
            resetForm();
          }}
          className="px-5 bg-transparent text-primary">
          Cancel
        </Button>
        <Button form="whatsapp-templates" type="submit" className="px-5 bg-primary border-primary">
          {isEdit ? 'Update' : 'Save'}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

AddEditMetaFlowModal.propTypes = {
  isOpen: PropTypes.bool,
  isEdit: PropTypes.bool,
  onCloseClick: PropTypes.func,
  editRecord: PropTypes.any
};

export default AddEditMetaFlowModal;
